/**
 * The table's voice: who taunts, who pulls a face, and who shows a hand.
 *
 * ============================== WHAT THIS IS ===================================
 * A module beside `useScene`, and built the same way and for the same reason: it reads
 * the PRESENTED step log — the events the flight layer and the payoff already read —
 * and turns them into three driver-side events. It does not touch `director.ts`, whose
 * timeline is not edited for looks; it does not touch the engine; and nothing it emits
 * ever reaches the wire or changes a decision. `tests/cosmetics.test.ts`'s promise
 * that a cosmetic is purely visual is a promise about this module too.
 *
 *     { t: "taunt",      seat, id }              a bubble over a seat
 *     { t: "expression", seat, expression }      the avatar swaps face
 *     { t: "show",       seat, cards }           two cards turn over on the felt
 *
 * Plain JSON, exactly like the Director's own synthetic `to_act`.
 *
 * ## Where the triggers come from
 *
 * Only from things the table has already SHOWN. The Phase 5 report's §6 is the list of
 * what the engine exposes and when, and the one line that governs this file is that
 * `Game.reveal()` throws until the session is finished — so Voice cannot ask the engine
 * what anybody is. It does not have to: the CLIENT chose the bot profiles when it built
 * the table (`Play.tsx` calls `tableOf`), so the archetype of every seat is a fact the
 * client already holds, and this takes it as a constructor argument rather than
 * inventing one or prising one out of the engine mid-hand.
 *
 * Everything else is read off the steps: a `push` says who won a pot and how big; the
 * `action` steps say whether the hero folded to a bet; the stacks at `end` against the
 * stacks at `shuffle` say who busted and who doubled. Nothing is read from a seat's
 * hole cards, ever, except `Game.liveHoles` after the hand is over, which is the one
 * accessor that exists for exactly this and refuses while a hand is live.
 *
 * ## The three cooldowns, and why each exists
 *
 *   - 90 SECONDS PER SEAT. One bot with a lot to say is a bot nobody can stand.
 *   - ONE BOT TAUNT PER HAND. Two bubbles over one pot read as a bug.
 *   - NOT DURING THE FIRST ORBIT of a sitting. A table that starts talking before
 *     everyone has had the button is a table that talks over the deal.
 *
 * The HERO's own tray is one taunt per 20 seconds and is not subject to the other two:
 * a player who wants to say something about the hand they just lost should be able to.
 *
 * ## Determinism
 *
 * The probabilities are drawn from a driver RNG of this module's own — mulberry32 from
 * `director.ts`, seeded from the session commitment with a domain suffix — so the same
 * session watched twice taunts the same way, and the engine's `handRng` is not touched
 * (which is what `tests/transport.test.ts` exists to keep true). The cooldowns are
 * wall-clock, so they are the one part a replay at a different pace can differ on;
 * `now()` is injectable so the tests do not have to wait ninety seconds.
 * ===============================================================================
 */
import type { TableState } from "@engine/table-state";
import type { Step } from "./director";
import { driverRng, seedFromString } from "./director";
import { derivePayoff } from "./payoff";
import type { Expression } from "../art/roster";
import { type ArchetypeId, type Mood, choicesFor } from "./lines";

/* -------------------------------------------------------------- the events --- */

export type VoiceEvent =
  | { t: "taunt"; seat: number; id: string }
  | { t: "expression"; seat: number; expression: Expression }
  | { t: "show"; seat: number; cards: readonly number[] };

/* ------------------------------------------------------- the trigger table --- */

/** What just happened that somebody might have something to say about. */
export type TriggerId = "big-pot" | "steal" | "bust" | "double" | "hero-timeout";

export interface Trigger {
  id: TriggerId;
  /** The mood the line is drawn from. */
  mood: Mood;
  /** The face the seat pulls with it. */
  expression: Expression;
  /** The chance, 0..1, by archetype; `base` is every archetype not named. */
  chance: { base: number } & Partial<Record<ArchetypeId, number>>;
  /** One sentence, for the report's own table. */
  note: string;
}

/**
 * THE TRIGGER TABLE, as data.
 *
 * Straight out of `OVERHAUL-PLAN-2026-09-05.md` §5 and the Phase 6 brief. The two
 * named archetypes are the two the brief names: a Maniac says twice as much as anyone
 * about a pot nobody contested, and the Rock says nothing at all about one — which is
 * in character and is, honestly, a soft tell. The report raises that as a question.
 */
export const TRIGGERS: readonly Trigger[] = [
  {
    id: "big-pot", mood: "up", expression: "amused", chance: { base: 0.4 },
    note: "won a pot bigger than 30 big blinds",
  },
  {
    id: "steal", mood: "up", expression: "amused",
    chance: { base: 0.25, maniac: 0.5, nit: 0 },
    note: "the hero folded to a bet and this seat took it uncontested",
  },
  {
    id: "bust", mood: "down", expression: "stare", chance: { base: 1 },
    note: "this seat ran out of chips — always, and always a stare",
  },
  {
    id: "double", mood: "up", expression: "amused", chance: { base: 0.3 },
    note: "this seat doubled up through the hero",
  },
  {
    id: "hero-timeout", mood: "nudge", expression: "amused", chance: { base: 0.15 },
    note: "the hero's clock ran out and acted for them",
  },
];

const TRIGGER_BY_ID = new Map<TriggerId, Trigger>(TRIGGERS.map((t) => [t.id, t]));

export function chanceFor(trigger: TriggerId, archetype: ArchetypeId | null): number {
  const t = TRIGGER_BY_ID.get(trigger);
  if (!t) return 0;
  if (archetype && archetype in t.chance) {
    return (t.chance as Record<string, number>)[archetype]!;
  }
  return t.chance.base;
}

/* ---------------------------------------------------------- showing a hand --- */

/**
 * Who shows an uncontested win, and how often. Decided 7 Sep 2026.
 *
 * A Maniac shows the bluff because the bluff is the point of being a Maniac. A Whale
 * shows because a Whale is having a good time. A Regular shows a monster and nothing
 * else. A Rock never shows anything, and neither does anybody stronger — showing gives
 * information away, and that is what "regular and up" means.
 */
export const SHOW_CHANCE: Readonly<Partial<Record<ArchetypeId, number>>> = {
  maniac: 0.4,
  whale: 0.2,
  tag: 0.15,
};

/** A Regular only shows a MONSTER; everybody else who shows, shows anything. */
export const SHOWS_ONLY_MONSTERS: readonly ArchetypeId[] = ["tag"];

/* ----------------------------------------------------------------- limits --- */

export const COOLDOWN = {
  /** Per seat, milliseconds. */
  seat: 90_000,
  /** The hero's tray. */
  hero: 20_000,
} as const;

/**
 * How many hands the table stays quiet for at the start of a sitting.
 *
 * PHASE 7, BOX 0(3). Phase 6 held the bots silent for one full orbit — nine hands at
 * nine-handed, four or five minutes before anybody says a word, which is right for an
 * hour-long sitting and wrong for a demo or the twenty-minute Campaign stage. The
 * planning review's answer: one orbit up to six seats, half an orbit (rounded up)
 * above. So 2 -> 2, 6 -> 6, 7 -> 4, 9 -> 5.
 */
export function firstOrbit(seats: number): number {
  return seats <= 6 ? seats : Math.ceil(seats / 2);
}

/**
 * At most three bubbles up at once; the rest wait, and give up if they wait too long.
 *
 * PHASE 7, BOX 0(5). Pure — no timers, no React — so the rule is tested in Node and
 * `useVoice` owns only the clock. `offer` is called when a taunt arrives, `release`
 * when a bubble's lifetime ends; both return what to put on screen now. A bubble that
 * has queued for longer than `maxWait` (the hold, `tauntHold`) is dropped rather than
 * shown late: a remark about a pot that has already been pushed is a remark about
 * nothing.
 *
 * The hero's own taunt goes to the FRONT of the queue. It is still subject to the cap —
 * three is three — but a player who pressed a button waits behind nobody. A seat that
 * already has a bubble up is replaced in place and costs no slot.
 */
export class BubbleGate<T> {
  private readonly up = new Set<number>();
  private queue: { seat: number; item: T; at: number }[] = [];
  constructor(readonly max = 3, public maxWait = 2000) {}

  /** A taunt arrived. Returns it if it may go up now, or null if it queued. */
  offer(seat: number, item: T, now: number, hero = false): { seat: number; item: T } | null {
    if (this.up.has(seat) || this.up.size < this.max) { this.up.add(seat); return { seat, item }; }
    /* One entry per seat: a newer remark from the same seat replaces its queued one. */
    this.queue = this.queue.filter((q) => q.seat !== seat);
    const entry = { seat, item, at: now };
    if (hero) this.queue.unshift(entry); else this.queue.push(entry);
    return null;
  }

  /** A bubble came down. Returns the queued ones that go up in its place, in order. */
  release(seat: number, now: number): { seat: number; item: T }[] {
    this.up.delete(seat);
    const out: { seat: number; item: T }[] = [];
    while (this.up.size < this.max && this.queue.length > 0) {
      const q = this.queue.shift()!;
      if (now - q.at > this.maxWait) continue;   /* waited too long: dropped */
      if (this.up.has(q.seat)) continue;
      this.up.add(q.seat);
      out.push({ seat: q.seat, item: q.item });
    }
    return out;
  }

  /** How many are up, and how many are waiting. For the tests and the walk. */
  get showing(): number { return this.up.size; }
  get waiting(): number { return this.queue.length; }

  clear(): void { this.up.clear(); this.queue = []; }
}

export interface VoiceOptions {
  seats: number;
  /** The viewer's seat. -1 for a bot-only table. */
  hero: number;
  /** Archetype per seat, as the CLIENT chose them. Null for the hero and for unknowns. */
  archetypes: readonly (ArchetypeId | null)[];
  /** Seeds this module's own RNG. The session commitment, normally. */
  seed: string | number;
  /** Table talk: false emits nothing at all. Read on every decision. */
  enabled: () => boolean;
  /** Injectable so the tests do not wait ninety seconds. */
  now?: () => number;
  /**
   * Any seat's cards, once the hand is done — `Game.liveHoles`. Omitted, nobody shows.
   * The signature is the engine's: null while a hand is live, which is the whole
   * reason it is safe to hand a renderer.
   */
  holes?: (seat: number) => readonly number[] | null;
  /** Is this hand's board a monster for this seat? For the Regular's rule. */
  strength?: (seat: number) => number | null;
}

/* ------------------------------------------------------------------- Voice --- */

export class Voice {
  private readonly o: Required<Pick<VoiceOptions, "seats" | "hero" | "archetypes" | "enabled">> & VoiceOptions;
  private readonly rng: () => number;
  private readonly now: () => number;
  private readonly subs = new Set<(e: VoiceEvent) => void>();

  /** Last taunt per seat, in `now()` milliseconds. */
  private lastTaunt: Record<number, number> = {};
  private lastHero = -Infinity;
  /** A bot has already spoken this hand. */
  private spokeThisHand = false;
  /** The hand the presented table is on, and the stacks it started with. */
  private handNumber = 0;
  private stacksAtStart: number[] = [];
  /** Did the hero fold to a BET this hand? The one fact the steal trigger needs. */
  private heroFoldedToABet = false;
  /** Did a human press a button for the current decision? Set by the route. */
  private heroPressed = false;
  /** Seats that have already shown this hand, so a show is never drawn twice. */
  private shown = new Set<number>();
  /** Phase 7 box 0(4): the last seat that was asked to nudge a timed-out hero. */
  private lastNudger = -1;

  constructor(opts: VoiceOptions) {
    this.o = opts as Voice["o"];
    this.now = opts.now ?? (() => Date.now());
    this.rng = driverRng(
      typeof opts.seed === "number" ? opts.seed : seedFromString(`${opts.seed}:voice`),
    );
  }

  subscribe(fn: (e: VoiceEvent) => void): () => void {
    this.subs.add(fn);
    return () => { this.subs.delete(fn); };
  }

  private emit(e: VoiceEvent): void {
    if (!this.o.enabled()) return;
    for (const s of this.subs) s(e);
  }

  /** The viewer's seat. Phase 7: the bubble queue puts the player's own taunt first. */
  get heroSeat(): number { return this.o.hero; }

  /** The route calls this whenever the PLAYER presses an action button. */
  heroPress(): void { this.heroPressed = true; }

  /* ------------------------------------------------------------- the hero --- */

  /** Milliseconds until the tray is usable again. 0 when it is usable now. */
  heroCooldownLeft(): number {
    return Math.max(0, COOLDOWN.hero - (this.now() - this.lastHero));
  }

  /**
   * The hero's own taunt, straight from the tray. Returns whether it was sent.
   *
   * Not subject to the per-hand rule or the first-orbit rule: those exist to stop the
   * TABLE talking over itself, and the player pressing a button is not the table.
   */
  heroTaunt(id: string): boolean {
    if (!this.o.enabled()) return false;
    if (this.o.hero < 0) return false;
    if (this.heroCooldownLeft() > 0) return false;
    this.lastHero = this.now();
    this.emit({ t: "taunt", seat: this.o.hero, id });
    return true;
  }

  /** The hero shows, from the band's Show control. `cards` is what to turn over. */
  heroShow(cards: readonly number[]): boolean {
    if (this.o.hero < 0 || cards.length === 0) return false;
    if (this.shown.has(this.o.hero)) return false;
    this.shown.add(this.o.hero);
    /* A show is not table talk: it is an action at a card table, and muting the
       bubbles does not mean the player cannot turn their own hand face up. */
    for (const s of this.subs) s({ t: "show", seat: this.o.hero, cards: [...cards] });
    return true;
  }

  /* -------------------------------------------------------------- the bots --- */

  /**
   * One step of the presented timeline. Called exactly where `useScene` is called.
   *
   * `before` is the table as it was when the step was announced, which is what makes
   * the reads honest: at a `push` the pot is still on the felt, and at `end` the
   * stacks are the ones the hand finished with.
   */
  onStep(step: Step, before: TableState): void {
    switch (step.kind) {
      case "shuffle": {
        this.handNumber = before.handNumber + 1;
        this.stacksAtStart = before.seats.map((s) => s.stack);
        this.spokeThisHand = false;
        this.heroFoldedToABet = false;
        this.heroPressed = false;
        this.shown.clear();
        break;
      }
      case "await":
        /* A fresh decision: nobody has pressed anything for it yet. */
        if (step.seat === this.o.hero) this.heroPressed = false;
        break;
      case "action": {
        if (step.seat === this.o.hero) {
          if (step.action.kind === "fold") {
            const own = before.seats[this.o.hero]?.committed ?? 0;
            this.heroFoldedToABet = before.currentBet > own;
          }
          /* The clock acted for them: `expire()` goes through the same path a click
             does, so the only thing that tells them apart is whether a click happened. */
          if (!this.heroPressed) this.fire("hero-timeout", this.pickTalker(before));
        }
        break;
      }
      case "push": {
        const seat = step.seat;
        if (seat === this.o.hero) break;
        const bb = before.rules.bigBlind || 1;
        const payoff = derivePayoff(before);
        if (step.amount / bb > 30) this.fire("big-pot", seat);
        else if (payoff?.uncontested && this.heroFoldedToABet) this.fire("steal", seat);
        /* And the chance to show it, which is a separate roll from the taunt's. */
        if (payoff?.uncontested) this.maybeShow(seat);
        break;
      }
      case "end": {
        for (let i = 0; i < before.seats.length; i++) {
          if (i === this.o.hero) continue;
          const started = this.stacksAtStart[i] ?? 0;
          const now = before.seats[i]?.stack ?? 0;
          if (started > 0 && now <= 0) { this.fire("bust", i); continue; }
          /* Doubled THROUGH the hero: they gained about what they had, and the hero
             is poorer than they started. A bot that doubled through another bot has
             nothing to say to the player about it. */
          const heroBefore = this.stacksAtStart[this.o.hero] ?? 0;
          const heroNow = before.seats[this.o.hero]?.stack ?? 0;
          if (started > 0 && now >= started * 2 && heroNow < heroBefore) this.fire("double", i);
        }
        break;
      }
      default:
        break;
    }
  }

  /* --------------------------------------------------------------- internals --- */

  /**
   * Who says something when the hero's clock runs out.
   *
   * PHASE 7, BOX 0(4): it ROTATES among the bots still in the hand. Phase 6 took the
   * first bot clockwise of the hero, which at a fixed table is always the same person —
   * the same face nudging the player every time, which reads as that character having
   * a grudge. Now each time-out starts looking one seat further round than the last
   * one did, among the seats that have not folded (a player who has left the hand has
   * nothing to wait for). The first time-out of a sitting still goes to the seat
   * immediately clockwise of the hero — the one whose turn it now is.
   *
   * No randomness: the rotation is a pointer, so the voice's own RNG stream is
   * untouched and a replay nudges from the same seats in the same order.
   */
  private pickTalker(before: TableState): number {
    const n = this.o.seats;
    const bots: number[] = [];
    for (let k = 1; k < n; k++) {
      const seat = (this.o.hero + k) % n;
      if (seat !== this.o.hero && this.o.archetypes[seat]) bots.push(seat);
    }
    const live = bots.filter((s) => {
      const x = before.seats[s];
      return !!x && !x.folded && !x.sittingOut;
    });
    const pool = live.length ? live : bots;
    if (pool.length === 0) return -1;
    /* Clockwise distance from the hero, so "after the last one" wraps correctly. */
    const d = (s: number) => (s - this.o.hero + n) % n;
    const last = this.lastNudger < 0 ? 0 : d(this.lastNudger);
    const next = pool.find((s) => d(s) > last) ?? pool[0]!;
    this.lastNudger = next;
    return next;
  }

  /** The gates, then the roll, then the line. */
  private fire(trigger: TriggerId, seat: number): void {
    if (seat < 0 || seat === this.o.hero) return;
    if (!this.o.enabled()) return;
    if (this.spokeThisHand) return;
    /* Not while the button is still going round for the first time — or, at a big
       table, the first half of the way round (Phase 7 box 0(3), `firstOrbit`). */
    if (this.handNumber <= firstOrbit(this.o.seats)) return;
    const last = this.lastTaunt[seat] ?? -Infinity;
    if (this.now() - last < COOLDOWN.seat) return;

    const archetype = this.o.archetypes[seat] ?? null;
    const t = TRIGGER_BY_ID.get(trigger)!;
    const chance = chanceFor(trigger, archetype);
    /* The roll happens for every candidate, chance or no chance, so that a table's
       RNG stream does not depend on which archetypes happen to be seated — a nit at
       seat three would otherwise change what seat four says. */
    const roll = this.rng();
    const pickRoll = this.rng();
    if (!(roll < chance)) return;

    /* Phase 7 box 0(6): `up` draws from the shared pool; `down` and `nudge` from the
       archetype's own lines. One pick roll either way, so the stream is unchanged. */
    const set = archetype ? choicesFor(archetype, t.mood) : [];
    if (set.length === 0) return;
    const chosen = set[Math.floor(pickRoll * set.length) % set.length]!;

    this.lastTaunt[seat] = this.now();
    this.spokeThisHand = true;
    this.emit({ t: "taunt", seat, id: chosen.id });
    this.emit({ t: "expression", seat, expression: t.expression });
  }

  /**
   * A bot showing an uncontested win, in character.
   *
   * `Game.liveHoles(seat)` is the source and it returns null while a hand is live, so
   * the roll can be made here and still be refused by the engine if the timing is
   * wrong — which is the right way round. A show is followed by the seat's expression,
   * and may be followed by a taunt (the same cooldown; `fire` decides).
   */
  private maybeShow(seat: number): void {
    if (!this.o.holes || this.shown.has(seat)) return;
    const archetype = this.o.archetypes[seat] ?? null;
    if (!archetype) return;
    const chance = SHOW_CHANCE[archetype] ?? 0;
    const roll = this.rng();
    if (!(roll < chance)) return;
    if (SHOWS_ONLY_MONSTERS.includes(archetype)) {
      const s = this.o.strength?.(seat) ?? null;
      if (s === null || s < 1) return;
    }
    const cards = this.o.holes(seat);
    if (!cards || cards.length !== 2) return;
    this.shown.add(seat);
    for (const fn of this.subs) fn({ t: "show", seat, cards: [...cards] });
    this.emit({ t: "expression", seat, expression: "amused" });
  }

  /**
   * The showdown's own expressions: the winner is amused, the biggest loser stares.
   *
   * Fired without a taunt and without a cooldown, because it is not talking — it is a
   * face, and everybody at a card table has one. Called by the scene at the payoff
   * moment, which is where the verdict already hangs.
   */
  showdownFaces(state: TableState): VoiceEvent[] {
    const p = derivePayoff(state);
    if (!p || p.uncontested) return [];
    const out: VoiceEvent[] = [];
    for (const w of p.winners) out.push({ t: "expression", seat: w, expression: "amused" });
    /* The biggest loser: of the seats that showed and lost, the one that had the most
       in front of them. One stare, not four — four staring seats is a cartoon. */
    let worst = -1, most = -Infinity;
    for (const l of p.losers) {
      const c = state.seats[l]?.committed ?? 0;
      if (c > most) { most = c; worst = l; }
    }
    if (worst >= 0) out.push({ t: "expression", seat: worst, expression: "stare" });
    for (const e of out) this.emit(e);
    return out;
  }
}
